import { PowerpointExportDto, PowerpointSlideType, AvailableTemplate } from '@ecosystem/export-api';
import html2canvas from 'html2canvas';
import { DateTime } from 'luxon';
import {
  Component,
  ComponentHeading,
  DataColumn,
  ExportOptions,
  GroupedListBoxMenuItem,
  ListBoxMenuItem,
  MenuItemDataColumns,
  MenuItemOption,
  MenuItemWithContext,
  PageDsl,
  Params,
  TitleSelector,
} from '../../types';
import { getExportContext } from './getExportContext';
import { logger } from '@ecosystem/newron-client-lib';
import { getExportHeadingTitle } from './pptUtils';

const getComponentTitle = (
  headingTitle: ComponentHeading['title'],
  componentSettings: Params,
  allColumns: DataColumn[]
): string => {
  if (typeof headingTitle === 'string') return headingTitle;

  const componentTitle = headingTitle.map(titlePart =>
    typeof titlePart === 'string' ? titlePart : getTitleFromMenu(titlePart, componentSettings, allColumns)
  );
  return componentTitle.filter(Boolean).join(' ');
};

const getTitleFromMenu = (
  titleSelector: TitleSelector,
  componentSettings: Params,
  allColumns: DataColumn[]
): string => {
  const { paramName, items } = titleSelector.menu;
  const paramValue = componentSettings[paramName]?.[0];

  for (const item of items) {
    const label = resolveMenuItemLabel(item as MenuItemOption, paramValue, allColumns);
    if (label) return label;
  }

  logger.warning('[componentToPptData] Menu type not supported');
  return '';
};

const resolveMenuItemLabel = (
  item: MenuItemOption | MenuItemDataColumns | MenuItemWithContext | GroupedListBoxMenuItem,
  paramValue: any,
  allColumns: DataColumn[]
): string | undefined => {
  if ((item.type === 'MenuItem' || item.type === 'MenuItemContext') && !Array.isArray(item.value)) {
    return item.value === paramValue ? item.label : undefined;
  }

  if (item.type === 'MenuItem' && Array.isArray(item.value)) {
    return getSubMenuLabel(paramValue, item.value);
  }

  if (item.type === 'DataColumns') {
    return getColumnLabel(paramValue, allColumns);
  }

  if (item.type === 'MenuItemGrouped') {
    return getGroupedMenuLabel(
      paramValue,
      item.list.filter((subItem): subItem is ListBoxMenuItem => subItem.type === 'MenuItem')
    );
  }

  return undefined;
};

const getColumnLabel = (paramValue: any, allColumns: DataColumn[]): string | undefined =>
  allColumns.find(col => col.name === paramValue)?.label;

const getSubMenuLabel = (paramValue: any, subMenuItems: ListBoxMenuItem[]): string | undefined =>
  subMenuItems.find(subItem => subItem.value === paramValue)?.label;

const getGroupedMenuLabel = (paramValue: any, groupedItems: ListBoxMenuItem[]): string | undefined =>
  groupedItems.find(subItem => subItem.value === paramValue)?.label;

const componentToPptData = async (
  dsl: PageDsl,
  { fileHeaderSettings, fileName, contentName }: ExportOptions,

  component: Component,
  exportContentRef: HTMLElement[] | HTMLElement,
  componentSettings: Params,
  allColumns: DataColumn[]
): Promise<PowerpointExportDto> => {
  const headingTitle = component?.heading?.title;
  const sharedBrands = dsl?.settings?.values?.sharedBrands || [];
  const clonedHeadingTitle = getExportHeadingTitle(headingTitle, sharedBrands);
  let slideTitle = getComponentTitle(clonedHeadingTitle, componentSettings, allColumns) ?? '';
  let slideSubtitle = component?.heading?.subTitle ?? contentName ?? '';
  const pptSlides = [];

  const elementRefs = Array.isArray(exportContentRef) ? exportContentRef : [exportContentRef];
  for (const elementRef of elementRefs) {
    const canvas = await html2canvas(elementRef);
    const dataUrl = canvas.toDataURL('image/png');

    pptSlides.push({
      type: PowerpointSlideType.Image,
      image: { dataUrl: dataUrl, width: elementRef.scrollWidth, height: elementRef.scrollHeight },
      title: slideTitle,
      subtitle: slideSubtitle,
      context: getExportContext(fileHeaderSettings),
    });
  }

  return {
    name: fileName,
    userDateTimeIso8601: DateTime.local().toISO(),
    template: AvailableTemplate.Powerpoint,
    slides: pptSlides,
  };
};

export { componentToPptData, getComponentTitle };
